import { FC, useMemo } from 'react';
import {
  Dropdown,
  Icon,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TableCell,
  Tag,
} from '@pypestream/design-system';
import { ProductName } from '@pypestream/api-services';
import { RenderViaTeamDetails } from './render-via-team-details';
import './permissions-table.scss';
import {
  HandleUpdateProjectProductRole,
  AccountProject,
} from './permissions-table.types';

export type ProjectProductRoleCellProps = {
  project: AccountProject;
  productId: string;
  canManageAccess: boolean;
  isSavingDetails: boolean;
  handleUpdateProjectProductRole: HandleUpdateProjectProductRole;
};

export const ProjectProductRoleCell: FC<ProjectProductRoleCellProps> = ({
  project,
  productId,
  canManageAccess,
  isSavingDetails,
  handleUpdateProjectProductRole,
}) => {
  const availableProductRoles = project.availableProjectRoles?.filter(
    (role) => role.productId === productId
  );
  const hasDirectRole = project._account_project_roles.find(
    (_account_project_role) =>
      !_account_project_role.teams &&
      _account_project_role.role.productId === productId
  );
  const rolePills = useMemo(
    () =>
      project._account_project_roles
        .map((_account_project_role) => {
          const isTeamInheritedRole =
            _account_project_role.teams &&
            _account_project_role.teams.length > 0;
          const roleSelectorId = `${project.project.id}-${productId}-${_account_project_role.role.id}-${
            isTeamInheritedRole ? 'inherited-role' : 'direct-role'
          }`;

          const canSelectMoreRole =
            !isTeamInheritedRole &&
            availableProductRoles?.length &&
            canManageAccess;

          return (
            (productId === _account_project_role.role.productId && (
              <Tag
                minimal
                removable={canManageAccess && !isTeamInheritedRole}
                key={roleSelectorId}
                {...(canManageAccess &&
                  canSelectMoreRole && {
                    id: roleSelectorId,
                    interactive: !!canSelectMoreRole && !isSavingDetails,
                  })}
                new-variant="outlined"
                onRemove={() => {
                  handleUpdateProjectProductRole({
                    orgId: project.project.accountId,
                    projectId: project.project.id,
                    role: {
                      id: _account_project_role.role.id,
                      name: _account_project_role.role.name || '',
                      productId: _account_project_role.role.productId || '',
                      product: {
                        id: _account_project_role.role.productId || '',
                        name: (_account_project_role.role.product?.name ||
                          '') as ProductName,
                        displayName:
                          _account_project_role.role.product?.displayName || '',
                      },
                    },
                    operation: 'remove',
                  });
                }}
                disabled={isSavingDetails}
              >
                <Icon name={isTeamInheritedRole ? 'teams' : 'users'}></Icon>
                {_account_project_role.role.name}
                {isTeamInheritedRole && (
                  <RenderViaTeamDetails
                    teams={
                      (_account_project_role.teams &&
                        _account_project_role.teams) ||
                      []
                    }
                  />
                )}
                {(canSelectMoreRole && (
                  <div>
                    <Icon name="chevron-down"></Icon>
                    <Dropdown trigger={roleSelectorId} placement="bottom-start">
                      <Menu>
                        {availableProductRoles?.map((availableRole) => (
                          <MenuItem
                            key={availableRole.id}
                            onClick={() => {
                              handleUpdateProjectProductRole({
                                orgId: project.project.accountId,
                                projectId: project.project.id,
                                role: availableRole,
                                operation: 'update',
                              });
                            }}
                            disabled={isSavingDetails}
                          >
                            {availableRole.name}
                          </MenuItem>
                        ))}
                      </Menu>
                    </Dropdown>
                  </div>
                )) ||
                  null}
              </Tag>
            )) ||
            null
          );
        })
        .filter((rolePill) => !!rolePill),
    [
      availableProductRoles,
      canManageAccess,
      productId,
      project._account_project_roles,
      project.project.accountId,
      project.project.id,
      isSavingDetails,
      handleUpdateProjectProductRole,
    ]
  );

  return (
    <TableCell>
      <Stack alignItems="center" gutter="xsmall">
        {rolePills}
        {/* Ability to add direct role only if no direct role is assigned to product*/}
        {canManageAccess && !hasDirectRole && (
          <div>
            <IconButton
              name="add"
              id={`${project.project.id}-${productId}`}
              disabled={isSavingDetails}
            />
            <Dropdown
              closeOnClickOutside
              trigger={`${project.project.id}-${productId}`}
              placement="bottom-start"
            >
              <Menu>
                {/* @todo - Fix this as it repeated code and will become hard to maintain */}
                {availableProductRoles?.map((availableRole) => (
                  <MenuItem
                    key={availableRole.id}
                    onClick={(e) => {
                      e.preventDefault();
                      handleUpdateProjectProductRole({
                        orgId: project.project.accountId,
                        projectId: project.project.id,
                        role: availableRole,
                        operation: 'add',
                      });
                    }}
                    disabled={isSavingDetails}
                  >
                    {availableRole.name}
                  </MenuItem>
                ))}
              </Menu>
            </Dropdown>
          </div>
        )}
      </Stack>
    </TableCell>
  );
};
